/**
 * Momentum Voice — DataFabric read service
 * ----------------------------------------
 * The /voice page is display-only. It reads the company's voice agents, phone
 * numbers, and call history directly from DataFabric (the Toolbox/AppMarket
 * world that owns the agents), using the Toolbox token — same pattern as
 * momentumVoiceService.ts and the reference insurancegig-npm-ui app.
 *
 * All management actions (create/edit agent, buy/assign/release number, place
 * a call) live in the Toolbox app; the page redirects there instead.
 */

const DATAFABRIC_URL =
  process.env.REACT_APP_DataFabric_Url ||
  "https://datafabricapi-stage.insurancegig.com";

/** Toolbox Momentum Voice management page — all mutating actions redirect here.
 *  Base host comes from REACT_APP_TOOLBOX_URL; the voicesubscription path is appended. */
const TOOLBOX_BASE = (
  process.env.REACT_APP_TOOLBOX_URL ||
  "https://www.toolbox-stage.momentumamp.com"
).replace(/\/+$/, "");
export const TOOLBOX_VOICE_URL = `${TOOLBOX_BASE}/voicesubscription?origin=true`;

const AUTH_URL = (process.env.REACT_APP_Auth_Url || "").replace(/\/+$/, "");

/** Exchanges the AMS token for a Toolbox SSO token — same nctoken exchange
 *  pattern as macssotoken/momentumclawsso, just a different Toolbox endpoint. */
async function getToolboxSsoToken(): Promise<string> {
  const amsToken = localStorage.getItem("amsToken") || "";
  const res = await fetch(
    `${AUTH_URL}/api/account/toolboxssotoken?nctoken=${encodeURIComponent(amsToken)}`,
  );
  if (!res.ok)
    throw new Error(`Toolbox SSO token request failed: ${res.status}`);

  // API may return a plain string token or JSON { token: '...' }
  const text = await res.text();
  try {
    const data = JSON.parse(text);
    return typeof data === "string" ? data : (data.token as string);
  } catch {
    return text.trim();
  }
}

/** Open the Toolbox voice management page in a new tab, bridging an SSO
 *  session first so the user lands there already signed in. Falls back to
 *  the direct (unauthenticated) link if the SSO exchange fails. */
export async function redirectToToolbox(): Promise<void> {
  try {
    const ssoToken = await getToolboxSsoToken();
    window.open(
      `${TOOLBOX_BASE}/bridge?token=${encodeURIComponent(ssoToken)}&widget=voice`,
      "_blank",
      "noopener",
    );
  } catch (err) {
    console.error("Toolbox SSO bridge failed, opening direct link:", err);
    window.open(TOOLBOX_VOICE_URL, "_blank", "noopener");
  }
}

/** Toolbox-scoped auth — DataFabric voice endpoints resolve the company from
 *  this token (the Toolbox company that owns the agents). */
function getToolboxHeaders(): HeadersInit {
  const token = localStorage.getItem("toolboxToken") || "";
  return {
    "Content-Type": "application/json",
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

async function dfGet<T>(path: string, query?: string): Promise<T> {
  const url = query
    ? `${DATAFABRIC_URL}/api/IGIGMomentumVoice/${path}?${query}`
    : `${DATAFABRIC_URL}/api/IGIGMomentumVoice/${path}`;
  const response = await fetch(url, {
    method: "GET",
    headers: getToolboxHeaders(),
  });
  if (!response.ok)
    throw new Error(`DataFabric ${path} failed: ${response.status}`);
  return response.json() as Promise<T>;
}

/* ── DataFabric response shapes (ASP.NET camelCase) ────────────────────────── */

export interface DfVoiceAgent {
  id: number | null;
  companyId: number | null;
  agentName: string | null;
  welcomeMessage: string | null;
  systemMessage: string | null;
  voiceSelection: string | null;
  voiceMailMessage: string | null;
  elevenLabsAgentId: string | null;
  created: string | null;
}

export interface DfPhoneNumber {
  id: number | null;
  phoneNumberId: string | null;
  twilioSid?: string | null;
  sid?: string | null; 
  phoneNumber: string | null;
  label: string | null;
  provider: string | null;
  supportsInbound: boolean | null;
  supportsOutbound: boolean | null;
  assignedAgentId: string | null;
  assignedAgentName: string | null;
  created: string | null;
}

export interface DfCallHistory {
  id: number | null;
  callSid: string | null;
  conversationId: string | null;
  agentId: string | null;
  phoneNumber: string | null;
  firstName: string | null;
  lastName: string | null;
  direction: string | null; // "Inbound" | "Outbound"
  callDurationSecs: string | null;
  callStatus: string | null;
  created: string | null;
  source: string | null;
  transcript: string | null;
  transcriptSummary: string | null;
  errorMessage: string | null;
  agentNumber: string | null;
  externalNumber: string | null;
}

/* ── Reads ─────────────────────────────────────────────────────────────────── */

/** All voice agents for the Toolbox company. */
export async function getVoiceAgentsDF(): Promise<DfVoiceAgent[]> {
  const data = await dfGet<DfVoiceAgent[]>("get/agents");
  return Array.isArray(data) ? data : [];
}

/** All purchased phone numbers for the company. */
export async function getPhoneNumbersDF(): Promise<DfPhoneNumber[]> {
  const data = await dfGet<DfPhoneNumber[]>("list/phonenumbers");
  return Array.isArray(data) ? data : [];
}

/** Full call history for the company. */
export async function getCallHistoryDF(): Promise<DfCallHistory[]> {
  const data = await dfGet<DfCallHistory[]>("list/call-history");
  return Array.isArray(data) ? data : [];
}

/** Single call detail by CallSid (for the read-only Call Detail modal). */
export async function getCallDetailDF(callSid: string): Promise<DfCallHistory> {
  return dfGet<DfCallHistory>(`call/${encodeURIComponent(callSid)}`);
}

/* ── Available numbers (Buy Phone Numbers) ─────────────────────────────────── */

export interface DfAvailableNumber {
  // Twilio snake_case fields
  phone_number?: string | null;
  friendly_name?: string | null;
  locality?: string | null;
  region?: string | null;
  postal_code?: string | null;
  iso_country?: string | null;
  capabilities?: {
    MMS?: boolean;
    SMS?: boolean;
    voice?: boolean;
    // legacy camelCase fallbacks
    mms?: boolean;
    sms?: boolean;
  } | null;
  // camelCase fallbacks (old shape)
  phoneNumber?: string | null;
  friendlyName?: string | null;
  postalCode?: string | null;
  isoCountry?: string | null;
}

export interface AvailableNumberSearchParams {
  areaCode?: string;    // 3-digit area code filter
  contains?: string;    // partial number / city / state
}

/**
 * Fetch available Twilio phone numbers for purchase.
 * Endpoint: GET /api/IGIGMomentumVoice/twilio/available-numbers/US
 * Optional query params: areaCode, contains
 */
export async function searchAvailableNumbers(
  params: AvailableNumberSearchParams = {},
): Promise<DfAvailableNumber[]> {
  const qs = new URLSearchParams();
  if (params.areaCode) qs.set("areaCode", params.areaCode);
  if (params.contains) qs.set("contains", params.contains);

  const url = `${DATAFABRIC_URL}/api/IGIGMomentumVoice/twilio/available-numbers/US${qs.toString() ? `?${qs.toString()}` : ""}`;

  const response = await fetch(url, {
    method: "GET",
    headers: getToolboxHeaders(),
  });

  if (!response.ok) throw new Error(`Available numbers failed: ${response.status}`);
  const data = await response.json();
  // API returns { available_phone_numbers: [...] }
  const list = data?.available_phone_numbers ?? data;
  return Array.isArray(list) ? list : [];
}

/** Purchase an available phone number for the company.
 *  POST /api/IGIGMomentumVoice/twilio/buy-number
 *  Body: { PhoneNumber, Label }
 */
export async function purchasePhoneNumber(
  phoneNumber: string,
  label: string = "",
): Promise<{ success: boolean; message?: string }> {
  const response = await fetch(
    `${DATAFABRIC_URL}/api/IGIGMomentumVoice/twilio/buy-number`,
    {
      method: "POST",
      headers: getToolboxHeaders(),
      body: JSON.stringify({ PhoneNumber: phoneNumber, Label: label }),
    },
  );
  if (!response.ok) {
    const text = await response.text().catch(() => "");
    throw new Error(text || `Purchase failed: ${response.status}`);
  }
  return response.json().catch(() => ({ success: true }));
}

/** Assign a voice agent to a phone number.
 *  PATCH /api/IGIGMomentumVoice/update/phonenumber/{phoneNumberId}
 *  Body: { AssignedAgentId }
 */
export async function assignAgentToNumber(
  phoneNumberId: string,
  agentId: string,
): Promise<{ success: boolean; message?: string }> {
  const response = await fetch(
    `${DATAFABRIC_URL}/api/IGIGMomentumVoice/update/phonenumber/${encodeURIComponent(phoneNumberId)}`,
    {
      method: "PATCH",
      headers: getToolboxHeaders(),
      body: JSON.stringify({ AssignedAgentId: agentId }),
    },
  );
  if (!response.ok) {
    const text = await response.text().catch(() => "");
    throw new Error(text || `Assign failed: ${response.status}`);
  }
  return response.json().catch(() => ({ success: true }));
}

/** Release (delete) a purchased phone number by its Twilio SID / phoneNumberId.
 *  DELETE /api/IGIGMomentumVoice/release-phone-number/{phoneNumberId}
 */
export async function releasePhoneNumber(
  phoneNumberId: string,
): Promise<{ message?: string }> {
  const response = await fetch(
    `${DATAFABRIC_URL}/api/IGIGMomentumVoice/release-phone-number/${encodeURIComponent(phoneNumberId)}`,
    {
      method: "DELETE",
      headers: getToolboxHeaders(),
    },
  );
  if (!response.ok) {
    const text = await response.text().catch(() => "");
    throw new Error(text || `Release failed: ${response.status}`);
  }
  return response.json().catch(() => ({ message: "Released" }));
}

/** Call recording audio → blob URL (caller must revoke). */
export async function getCallAudioDF(conversationId: string): Promise<string> {
  const response = await fetch(
    `${DATAFABRIC_URL}/api/IGIGMomentumVoice/call/recording/${encodeURIComponent(conversationId)}`,
    { method: "GET", headers: getToolboxHeaders() },
  );
  if (!response.ok)
    throw new Error(`Failed to fetch call audio: ${response.status}`);
  const blob = await response.blob();
  return URL.createObjectURL(blob);
}

/* ── Mutation helpers ────────────────────────────────────────────────────────── */

/** Headers for PATCH/POST mutations — explicit charset satisfies strict ASP.NET media-type checks. */
function getMutationHeaders(): HeadersInit {
  const token = localStorage.getItem("toolboxToken") || "";
  return {
    "Content-Type": "application/json; charset=utf-8",
    Accept: "application/json",
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

/* ── Voices ──────────────────────────────────────────────────────────────────── */

export interface DfVoice {
  voice_id: string;
  name: string | null;
  category?: string | null;
  preview_url?: string | null;
  labels?: {
    gender?: string | null;
    accent?: string | null;
    description?: string | null;
    age?: string | null;
    use_case?: string | null;
    [key: string]: string | null | undefined;
  } | null;
  gender?: string | null;
}

/** voice_type and category are the only fixed ElevenLabs enums — everything else
 *  (gender/age/accent/use_cases) matches free-text voice labels, so there's no
 *  universal list to validate against client-side; ElevenLabs just returns zero
 *  matches for a value it doesn't recognize. */
export type VoiceType =
  | "personal" | "community" | "default" | "workspace"
  | "non-default" | "non-community" | "saved";
export type VoiceCategory = "premade" | "cloned" | "generated" | "professional";

export interface VoiceSearchParams {
  search?: string;
  voiceType?: VoiceType;
  category?: VoiceCategory;
  gender?: string;
  age?: string;
  accent?: string;
  language?: string[];
  useCases?: string[];
  sort?: "created_at_unix" | "name";
  sortDirection?: "asc" | "desc";
  pageSize?: number;
  nextPageToken?: string;
}

export interface VoiceSearchResponse {
  voices: DfVoice[];
  has_more: boolean;
  next_page_token: string | null;
  total_count?: number;
}

function buildVoiceSearchQuery(params?: VoiceSearchParams): string {
  const parts: string[] = [];
  const add = (key: string, value?: string | number) => {
    if (value !== undefined && value !== null && value !== "") {
      parts.push(`${key}=${encodeURIComponent(String(value))}`);
    }
  };
  const addEach = (key: string, values?: string[]) => {
    (values ?? []).forEach((v) => v && parts.push(`${key}=${encodeURIComponent(v)}`));
  };

  add("search", params?.search);
  add("voiceType", params?.voiceType);
  add("category", params?.category);
  add("gender", params?.gender);
  add("age", params?.age);
  add("accent", params?.accent);
  addEach("language", params?.language);
  addEach("useCases", params?.useCases);
  add("sort", params?.sort);
  add("sortDirection", params?.sortDirection);
  add("pageSize", params?.pageSize);
  add("nextPageToken", params?.nextPageToken);

  return parts.join("&");
}

/** Available ElevenLabs voices — everything in the account's library (own +
 *  community, unless narrowed via voiceType/category), one page per call.
 *  GET /api/IGIGMomentumVoice/list/voices */
export async function getVoicesDF(params?: VoiceSearchParams): Promise<VoiceSearchResponse> {
  const query = buildVoiceSearchQuery(params);
  const data = await dfGet<VoiceSearchResponse | DfVoice[]>("list/voices", query);
  if (Array.isArray(data)) return { voices: data, has_more: false, next_page_token: null };
  return {
    voices: data?.voices ?? [],
    has_more: data?.has_more ?? false,
    next_page_token: data?.next_page_token ?? null,
    total_count: data?.total_count,
  };
}

/* ── Knowledge Base documents ────────────────────────────────────────────────── */

export interface DfKbDocumentRecord {
  id: number;
  fileId: string;
  fileName: string;
}

/** All KB documents for the company. GET /api/IGIGMomentumVoice/get/documents */
export async function getKbDocumentsDF(): Promise<DfKbDocumentRecord[]> {
  const data = await dfGet<DfKbDocumentRecord[]>("get/documents");
  return Array.isArray(data) ? data : [];
}

/** Upload a document to the Knowledge Base.
 *  POST /api/IGIGMomentumVoice/insert/document (multipart/form-data) */
export async function uploadKbDocumentDF(
  file: File,
): Promise<{ FileId: string; FileName: string }> {
  const token = localStorage.getItem("toolboxToken") || "";
  const formData = new FormData();
  formData.append("file", file);
  const response = await fetch(
    `${DATAFABRIC_URL}/api/IGIGMomentumVoice/insert/document`,
    {
      method: "POST",
      headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      body: formData,
    },
  );
  if (!response.ok) {
    const text = await response.text().catch(() => "");
    throw new Error(text || `Upload document failed: ${response.status}`);
  }
  return response.json();
}

/** Delete a KB document. DELETE /api/IGIGMomentumVoice/delete/document/{fileId} */
export async function deleteKbDocumentDF(
  fileId: string,
): Promise<{ Message?: string }> {
  const response = await fetch(
    `${DATAFABRIC_URL}/api/IGIGMomentumVoice/delete/document/${encodeURIComponent(fileId)}`,
    { method: "DELETE", headers: getMutationHeaders() },
  );
  if (!response.ok) {
    const text = await response.text().catch(() => "");
    throw new Error(text || `Delete document failed: ${response.status}`);
  }
  return response.json().catch(() => ({ Message: "Deleted" }));
}

/* ── Tools / Functions ────────────────────────────────────────────────────────── */

export interface DfToolRecord {
  id: number;
  elevenLabsToolId: string;
  toolName: string;
  toolDescription?: string | null;
  apiUrl?: string | null;
  apiMethod?: string | null;
}

export interface DfCreateToolPayload {
  ToolName: string;
  ToolDescription: string;
  ApiUrl: string;
  ApiMethod: string;
  ToolParameters: Array<{
    Identifier: string;
    DataType: string;
    Description: string;
    IsRequired: boolean;
  }>;
}

/** All company tools. GET /api/IGIGMomentumVoice/list/tools */
export async function getToolsDF(): Promise<DfToolRecord[]> {
  const data = await dfGet<DfToolRecord[]>("list/tools");
  return Array.isArray(data) ? data : [];
}

/** Create a new tool. POST /api/IGIGMomentumVoice/save/tool */
export async function createToolDF(
  payload: DfCreateToolPayload,
): Promise<{ elevenLabsToolId?: string; message?: string }> {
  const response = await fetch(
    `${DATAFABRIC_URL}/api/IGIGMomentumVoice/save/tool`,
    { method: "POST", headers: getMutationHeaders(), body: JSON.stringify(payload) },
  );
  if (!response.ok) {
    const text = await response.text().catch(() => "");
    throw new Error(text || `Create tool failed: ${response.status}`);
  }
  return response.json().catch(() => ({}));
}

/** Assign tools to an agent. PATCH /api/IGIGMomentumVoice/assign/tools/{agentId}
 *  Body: string[] of ElevenLabs tool IDs */
export async function assignToolsDF(
  elevenLabsAgentId: string,
  toolIds: string[],
): Promise<{ success: boolean; message?: string }> {
  const response = await fetch(
    `${DATAFABRIC_URL}/api/IGIGMomentumVoice/assign/tools/${encodeURIComponent(elevenLabsAgentId)}`,
    { method: "PATCH", headers: getMutationHeaders(), body: JSON.stringify(toolIds) },
  );
  if (!response.ok) {
    const text = await response.text().catch(() => "");
    throw new Error(text || `Assign tools failed: ${response.status}`);
  }
  return response.json().catch(() => ({ success: true }));
}

/* ── Call End Action ──────────────────────────────────────────────────────────── */

export interface DfCallEndAction {
  elevenLabsAgentId?: string | null;
  thirdPartyUrl?: string | null;
  thirdPartyMethod?: string | null;
  thirdPartyHeaders?: string | null;
  thirdPartyContentType?: string | null;
  thirdPartyAuthType?: string | null;
  thirdPartyAuthValue?: string | null;
  hasThirdPartyAuthValue?: boolean;
  thirdPartyPayloadTemplate?: string | null;
}

/** Get Call End Action config for an agent.
 *  GET /api/IGIGMomentumVoice/get/agent-call-action/{agentId} */
export async function getAgentCallActionDF(
  elevenLabsAgentId: string,
): Promise<DfCallEndAction> {
  return dfGet<DfCallEndAction>(`get/agent-call-action/${encodeURIComponent(elevenLabsAgentId)}`);
}

/* ── Agent detail ────────────────────────────────────────────────────────────── */

export interface DfKbDocument {
  id: string;
  name: string;
  type?: string | null;
  created_at?: string | null;
}

export interface DfAgentDetail {
  agent_id?: string | null;
  name?: string | null;
  conversation_config?: {
    agent?: {
      first_message?: string | null;
      language?: string | null;
      prompt?: {
        prompt?: string | null;
        llm?: string | null;
        temperature?: number | null;
        max_tokens?: number | null;
        knowledge_base?: Array<{ id: string; name: string; type?: string }> | null;
        tools?: Array<{
          type?: string;
          name?: string;
          description?: string;
          params?: {
            system_tool_type?: string;
            voicemail_message?: string | null;
            transfers?: Array<{ phone_number?: string | null; condition?: string | null }> | null;
          } | null;
        }> | null;
      } | null;
    } | null;
    tts?: { voice_id?: string | null; model_id?: string | null } | null;
  } | null;
  phone_numbers?: Array<{ phone_number?: string | null; label?: string | null; phone_number_id?: string | null }> | null;
  metadata?: { created_at_unix_secs?: number | null; updated_at_unix_secs?: number | null } | null;
  [key: string]: unknown;
}

/** Full ElevenLabs agent detail. GET /api/IGIGMomentumVoice/get/agent/{elevenLabsAgentId} */
export async function getAgentDetailDF(elevenLabsAgentId: string): Promise<DfAgentDetail> {
  return dfGet<DfAgentDetail>(`get/agent/${encodeURIComponent(elevenLabsAgentId)}`);
}

/* ── Create / Update / Delete agent ─────────────────────────────────────────── */

export interface DfCreateAgentPayload {
  AgentName: string;
  WelcomeMessage: string;
  SystemMessage: string;
  VoiceSelection: string;
  VoiceMailMessage: string;
  LlmModel: string;
  PurchasedPhoneNumbers?: string[] | null;
  AddedNumbers?: Array<{ Number: string; Condition: string }> | null;
}

export interface DfKbAssignment {
  type: string;
  name: string;
  id: string;
  usageMode: string;
}

export interface DfUpdateAgentPayload {
  AgentName: string;
  WelcomeMessage: string;
  SystemMessage: string;
  VoiceSelection: string;
  VoiceMailMessage: string;
  LlmModel: string;
  PurchasedPhoneNumbers: never[];
  AddedNumbers: never[];
  KnowledgeBases: DfKbAssignment[];
  ConversationConfig: Record<string, never>;
  ThirdPartyUrl: string | null;
  ThirdPartyMethod: string;
  ThirdPartyHeaders: string | null;
  ThirdPartyContentType: string;
  ThirdPartyAuthType: string;
  ThirdPartyAuthValue: string | null;
  ThirdPartyPayloadTemplate: string | null;
}

export interface DfTransferRule {
  TransferPhoneNumber: string;
  Condition: string;
  TransferType: string;
}

/** Create a new agent. POST /api/IGIGMomentumVoice/save/agents */
export async function createAgentDF(
  payload: DfCreateAgentPayload,
): Promise<{ success: boolean; agentId?: string; message?: string }> {
  const response = await fetch(
    `${DATAFABRIC_URL}/api/IGIGMomentumVoice/save/agents`,
    { method: "POST", headers: getMutationHeaders(), body: JSON.stringify(payload) },
  );
  if (!response.ok) {
    const text = await response.text().catch(() => "");
    throw new Error(text || `Create agent failed: ${response.status}`);
  }
  return response.json().catch(() => ({ success: true }));
}

/** Update an agent. PATCH /api/IGIGMomentumVoice/update/agent/{elevenLabsAgentId} */
export async function updateAgentDF(
  elevenLabsAgentId: string,
  payload: DfUpdateAgentPayload,
): Promise<{ success: boolean; message?: string }> {
  const response = await fetch(
    `${DATAFABRIC_URL}/api/IGIGMomentumVoice/update/agent/${encodeURIComponent(elevenLabsAgentId)}`,
    { method: "PATCH", headers: getMutationHeaders(), body: JSON.stringify(payload) },
  );
  if (!response.ok) {
    const text = await response.text().catch(() => "");
    throw new Error(text || `Update agent failed: ${response.status}`);
  }
  return response.json().catch(() => ({ success: true }));
}

/** Save transfer rules. PATCH /api/IGIGMomentumVoice/update/agent/transfers/{elevenLabsAgentId} */
export async function saveTransfersDF(
  elevenLabsAgentId: string,
  transfers: DfTransferRule[],
): Promise<{ success: boolean; message?: string }> {
  const response = await fetch(
    `${DATAFABRIC_URL}/api/IGIGMomentumVoice/update/agent/transfers/${encodeURIComponent(elevenLabsAgentId)}`,
    { method: "PATCH", headers: getMutationHeaders(), body: JSON.stringify(transfers) },
  );
  if (!response.ok) {
    const text = await response.text().catch(() => "");
    throw new Error(text || `Save transfers failed: ${response.status}`);
  }
  return response.json().catch(() => ({ success: true }));
}

/** Delete an agent. DELETE /api/IGIGMomentumVoice/delete/agent/{elevenLabsAgentId} */
export async function deleteAgentDF(
  elevenLabsAgentId: string,
): Promise<{ success: boolean; message?: string }> {
  const response = await fetch(
    `${DATAFABRIC_URL}/api/IGIGMomentumVoice/delete/agent/${encodeURIComponent(elevenLabsAgentId)}`,
    { method: "DELETE", headers: getMutationHeaders() },
  );
  if (!response.ok) {
    const text = await response.text().catch(() => "");
    throw new Error(text || `Delete agent failed: ${response.status}`);
  }
  return response.json().catch(() => ({ success: true }));
}
